// The blog comments plugin from Great Southern Websites. Put in place by the
// website-builder elf as web/comments/comments.js and bundled by the web
// bundler into its own bundle (quarkus.web-bundler.bundle.comments=true);
// templates/partials/comments.html loads it from the post layout. It renders
// in the light DOM so the site's own styles and theme tokens apply. A post
// is identified by Roq's page id (its source file), the same on every
// address the site has. Do not edit this file on a site: the elf replaces it
// with its current copy when comments are added.
import { LitElement, html, nothing } from 'lit';

const FAILED = 'Sorry, the comment could not be sent. Please try again in a moment.';

class GswComments extends LitElement {
  static properties = {
    site: { type: String },
    api: { type: String },
    post: { type: String },
    _data: { state: true },
    _state: { state: true },
    _error: { state: true },
    _sent: { state: true },
  };

  createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    this._state = 'loading';
    this._load();
  }

  _url() {
    return String(this.api || '').replace(/\/+$/, '') + '/v1/public/comments/' + encodeURIComponent(this.site || '')
      + '?post=' + encodeURIComponent(this.post || '');
  }

  async _load() {
    try {
      const res = await fetch(this._url(), { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('unavailable');
      this._data = await res.json();
      this._state = 'ready';
    } catch (e) {
      this._state = 'unavailable';
    }
  }

  async _submit(e) {
    e.preventDefault();
    const body = { postTitle: document.title, postUrl: window.location.href };
    for (const [k, v] of new FormData(e.target).entries()) body[k] = v;
    this._state = 'sending';
    this._error = '';
    try {
      const res = await fetch(this._url(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      let out = {};
      try { out = await res.json(); } catch (err) { /* no body */ }
      if (!res.ok) {
        this._error = out && out.error ? out.error : FAILED;
        this._state = 'ready';
        return;
      }
      this._sent = out && out.message ? out.message : 'Thank you, your comment has been sent.';
      if (out && out.comment) this._data = { ...this._data, comments: [...(this._data.comments || []), out.comment] };
      this._state = 'sent';
    } catch (err) {
      this._error = FAILED;
      this._state = 'ready';
    }
  }

  _date(iso) {
    try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }); } catch (e) { return ''; }
  }

  render() {
    if (this._state === 'loading' || this._state === 'unavailable') return nothing;
    const list = this._data.comments || [];
    return html`
      <section class="gsw-comments" aria-label="Comments">
        <h2>${list.length === 0 ? 'Comments' : list.length === 1 ? '1 comment' : list.length + ' comments'}</h2>
        ${list.length === 0 ? html`<p class="gsw-comments-note">No comments yet. Be the first.</p>` : nothing}
        ${list.map(c => html`
          <article class="gsw-comment">
            <p class="gsw-comment-meta"><strong>${c.name}</strong> <time datetime=${c.createdAt}>${this._date(c.createdAt)}</time></p>
            <p class="gsw-comment-text">${c.text}</p>
          </article>`)}
        ${this._state === 'sent'
          ? html`<p class="gsw-comments-sent" role="status">${this._sent}</p>`
          : this._form()}
      </section>`;
  }

  _form() {
    const sending = this._state === 'sending';
    return html`
      <form class="gsw-comment-form" @submit=${this._submit}>
        <h3>Leave a comment</h3>
        <p class="gsw-comment-field"><label for="gsw-comment-name">Your name</label><input id="gsw-comment-name" name="name" type="text" autocomplete="name" maxlength="100" required></p>
        <p class="gsw-comment-field"><label for="gsw-comment-email">Your email address (optional, never shown)</label><input id="gsw-comment-email" name="email" type="email" autocomplete="email"></p>
        <p class="gsw-comment-field"><label for="gsw-comment-text">Your comment</label><textarea id="gsw-comment-text" name="text" rows="5" maxlength=${this._data.maxChars || 2000} required></textarea></p>
        <div class="gsw-comments-trap" aria-hidden="true">
          <label>Leave this empty<input type="text" name="website" tabindex="-1" autocomplete="off"></label>
        </div>
        ${this._error ? html`<p class="gsw-comments-error" role="alert">${this._error}</p>` : nothing}
        ${this._data.moderated ? html`<p class="gsw-comments-note">Comments appear once they have been approved.</p>` : nothing}
        <p><button class="btn btn-primary" type="submit" ?disabled=${sending}>${sending ? 'Sending...' : 'Post comment'}</button></p>
      </form>`;
  }
}

customElements.define('gsw-comments', GswComments);
